import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PlannerContext } from "../../../../context/planner/PlannerContext";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SelfImprovementIcon from "@mui/icons-material/SelfImprovement";
import ChecklistIcon from "@mui/icons-material/Checklist";
import CheckIcon from "@mui/icons-material/Check";
import Button from "../../../input/button";
import {
  TimeRangeFields,
  isTimeRangeValid,
  ActivitySelector,
  TaskPicker,
} from "./shared";
import { ColourPicker } from "../../../pickers";
import { PICKER_COLORS } from "../../../../constants/pickerColors";

const BLOCK_TYPES = [
  {
    id: "time",
    label: "Time Block",
    description:
      "Reserve a chunk of time for any purpose. Give it a name and colour to stay organised.",
    icon: AccessTimeIcon,
  },
  {
    id: "task",
    label: "Task Block",
    description:
      "Assign specific tasks to a time slot so you know exactly what to work on and when.",
    icon: ChecklistIcon,
  },
  {
    id: "activity",
    label: "Activity Block",
    description:
      "Schedule time for one of your activities. Activities are things you do regularly that aren't one-off tasks.",
    icon: SelfImprovementIcon,
  },
];

function addHour(time) {
  const [hours, minutes] = time.split(":").map(Number);
  const newHours = (hours + 1) % 24;
  return `${String(newHours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

/**
 * Calculate the duration in minutes between two time strings (HH:MM format)
 */
function calculateBlockDurationMinutes(startTime, endTime) {
  const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
  if (!timeRegex.test(startTime) || !timeRegex.test(endTime)) {
    return null;
  }
  const [startHours, startMinutes] = startTime.split(":").map(Number);
  const [endHours, endMinutes] = endTime.split(":").map(Number);
  const startTotalMinutes = startHours * 60 + startMinutes;
  const endTotalMinutes = endHours * 60 + endMinutes;
  const duration = endTotalMinutes - startTotalMinutes;
  return duration > 0 ? duration : null;
}

const InsertBlock = () => {
  const navigate = useNavigate();
  const {
    activeTime,
    setActiveTime,
    setSelectedTool,
    addBlock,
    activities,
    tasks,
    blocks,
    closeToolPanel,
  } = useContext(PlannerContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [step, setStep] = useState(0);
  const [startTimeInputText, setStartTimeInputText] = useState(
    activeTime || "09:00",
  );
  const [endTimeInputText, setEndTimeInputText] = useState(
    addHour(activeTime || "09:00"),
  );
  const [blockType, setBlockType] = useState(null);
  const [blockName, setBlockName] = useState("");
  const [blockColor, setBlockColor] = useState(null);
  const [selectedActivityId, setSelectedActivityId] = useState("");
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [startTimeTouched, setStartTimeTouched] = useState(false);
  const [endTimeTouched, setEndTimeTouched] = useState(false);
  const [taskSearchQuery, setTaskSearchQuery] = useState("");

  const timesValid = isTimeRangeValid(startTimeInputText, endTimeInputText);

  // Update start time when activeTime changes
  useEffect(() => {
    if (activeTime) {
      setStartTimeInputText(activeTime);
      setEndTimeInputText(addHour(activeTime));
    }
  }, [activeTime]);

  useEffect(() => {
    return () => {
      setActiveTime(null);
    };
  }, [setActiveTime]);

  const handleCancel = () => {
    setActiveTime(null);
    setSelectedTool("dayConfig");
    if (isMobile) {
      closeToolPanel();
    }
  };

  const handleBack = () => {
    if (step === 0) {
      handleCancel();
    } else {
      setStep(0);
    }
  };

  const handleTypeSelect = (typeId) => {
    setBlockType(typeId);
    setBlockName("");
    setBlockColor(null);
    setSelectedActivityId("");
    setSelectedTaskIds([]);
    setStep(1);
  };

  const getDefaultName = () => {
    switch (blockType) {
      case "time":
        return "Time Block";
      case "activity":
        const activity = activities.find((a) => a.id === selectedActivityId);
        return activity?.name || "Activity";
      case "task":
        return "Tasks";
      default:
        return "New Block";
    }
  };

  const onSave = () => {
    if (!timesValid) return;

    const blockData = {
      type: blockType,
      startTime: startTimeInputText,
      endTime: endTimeInputText,
      name: blockName || getDefaultName(),
    };

    if (blockType === "time" && blockColor) {
      blockData.color = blockColor;
    }

    if (blockType === "activity") {
      blockData.activityId = selectedActivityId || null;
    }

    if (blockType === "task") {
      blockData.taskIds = selectedTaskIds;
    }

    addBlock(blockData);
    if (isMobile) {
      closeToolPanel();
    }
  };

  const selectedTypeInfo = BLOCK_TYPES.find((t) => t.id === blockType);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        flex: 1,
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          minHeight: 0,
          pb: 2,
        }}
      >
        <Box sx={{ py: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              "&:hover": { opacity: 0.7 },
            }}
            onClick={handleBack}
          >
            <ArrowBackIcon
              sx={{ mr: 1, fontSize: 20, color: "text.secondary" }}
            />
            <Typography variant="body2" color="text.secondary">
              {step === 0 ? "Cancel" : "Back"}
            </Typography>
          </Box>
          <Box sx={{ mb: 2 }} />
          {/* Step 0: Block type selection */}
          {step === 0 && (
            <Box>
              <Typography variant="h3">Add New Block</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                What type of block would you like to add?
              </Typography>
              <List sx={{ py: 0 }}>
                {BLOCK_TYPES.map((type) => {
                  const Icon = type.icon;
                  return (
                    <ListItem key={type.id} disablePadding sx={{ mb: 1.5 }}>
                      <ListItemButton
                        onClick={() => handleTypeSelect(type.id)}
                        sx={{
                          py: 2.5,
                          px: 2,
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "grey.300",
                          "&:hover": {
                            borderColor: "primary.main",
                            bgcolor: "rgba(25, 123, 104, 0.04)",
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 48 }}>
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: "grey.100",
                            }}
                          >
                            <Icon sx={{ color: "grey.600" }} />
                          </Box>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography fontWeight="bold" sx={{ mb: 0.5 }}>
                              {type.label}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="body2" color="text.secondary">
                              {type.description}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          )}

          {/* Step 1: Block configuration */}
          {step === 1 && (
            <Stack spacing={2}>
              <Typography variant="h3">
                New {selectedTypeInfo?.label || "Block"}
              </Typography>

              {blockType === "activity" && (
                <ActivitySelector
                  activities={activities}
                  value={selectedActivityId}
                  onChange={setSelectedActivityId}
                  blockDurationMinutes={calculateBlockDurationMinutes(
                    startTimeInputText,
                    endTimeInputText,
                  )}
                />
              )}
              {blockType === "time" && (
                <>
                  <TextField
                    label="Block Name (optional)"
                    value={blockName}
                    onChange={(e) => setBlockName(e.target.value)}
                    placeholder={getDefaultName()}
                    size="small"
                    fullWidth
                  />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Colour
                    </Typography>
                    <ColourPicker
                      selectedColor={blockColor || "#2196f3"}
                      onColorSelect={setBlockColor}
                      colors={PICKER_COLORS}
                      size={32}
                    />
                  </Box>
                </>
              )}

              <TimeRangeFields
                startTime={startTimeInputText}
                endTime={endTimeInputText}
                onStartTimeChange={setStartTimeInputText}
                onEndTimeChange={setEndTimeInputText}
                startTouched={startTimeTouched}
                endTouched={endTimeTouched}
                onStartBlur={() => setStartTimeTouched(true)}
                onEndBlur={() => setEndTimeTouched(true)}
              />

              {blockType === "task" && tasks && tasks.length > 0 && (
                <Box>
                  <TaskPicker
                    tasks={tasks}
                    selectedTaskIds={selectedTaskIds}
                    onTaskToggle={(taskId) => {
                      if (selectedTaskIds.includes(taskId)) {
                        setSelectedTaskIds(
                          selectedTaskIds.filter((id) => id !== taskId),
                        );
                      } else {
                        setSelectedTaskIds([...selectedTaskIds, taskId]);
                      }
                    }}
                    searchQuery={taskSearchQuery}
                    onSearchChange={setTaskSearchQuery}
                    headerText={`Select tasks (${selectedTaskIds.length} selected)`}
                    blocksData={blocks}
                  />
                </Box>
              )}
              {!isMobile && (
                <Box>
                  <Button
                    text="Add Block"
                    onClick={onSave}
                    fullWidth
                    color="orange"
                    disabled={!timesValid}
                  />
                </Box>
              )}
            </Stack>
          )}
        </Box>
      </Box>

      {isMobile && step === 1 && (
        <Box
          sx={{
            pt: 2,
            pb: 1,
            borderTop: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            flexShrink: 0,
          }}
        >
          <Button
            text="Add Block"
            onClick={onSave}
            fullWidth
            color="orange"
            disabled={!timesValid}
          />
        </Box>
      )}
    </Box>
  );
};

export default InsertBlock;
